import { getSnapshot } from "mobx-state-tree";
import { SpendingPaymentInfo } from "modules/orders-manage/models/spending-payment-info";
import { OrdersStoreType } from "modules/orders-manage/models/orders-store";
import {
  diffPlanPayment,
  diffSpendingRow,
  hasChanges,
  planPaymentFields,
  spendingFields,
} from "modules/orders-manage/models/spending-patch";

/** Секции состава затрат, строки которых сохраняются точечно */
export const SPENDING_SECTIONS = {
  outsourced: "outsourced",
  other: "other",
  trip: "trip",
};

/**
 * Сохранение одной строки затрат или одного планового платежа.
 * Сравнивает то, что сейчас в форме, с последним ответом сервера и отправляет разницу —
 * поэтому чужие правки соседних полей и статусы фин. запросов не затираются.
 */
export interface SpendingSaver {
  /** Строка изменена и её есть смысл сохранять */
  rowChanged: (section: string, spending: TStringMap<any>) => boolean;

  /** Сохранить строку целиком (без платежей) */
  saveRow: (section: string, spending: TStringMap<any>) => Promise<boolean>;

  /** Удалить строку */
  removeRow: (section: string, spending: TStringMap<any>) => Promise<boolean>;

  /** Платёж изменён и его есть смысл сохранять */
  paymentChanged: (section: string, spendingId: string, payment: TStringMap<any>) => boolean;

  /** Платёж уже существует на сервере */
  paymentSaved: (section: string, spendingId: string, guid: string) => boolean;

  /** Сохранить один плановый платёж */
  savePayment: (section: string, spendingId: string, payment: TStringMap<any>) => Promise<boolean>;

  /**
   * Сохранить платёж строки, которой может ещё не быть на сервере
   * (сотрудник добавил строку и сразу оплату)
   */
  savePaymentIn: (section: string, spending: TStringMap<any>, payment: TStringMap<any>) => Promise<boolean>;

  /** Удалить плановый платёж */
  removePayment: (section: string, spendingId: string, payment: TStringMap<any>) => Promise<boolean>;

  /** Кто, когда и откуда изменил строку последним */
  rowAuthor: (section: string, spendingId: string) => string;

  /** Есть ли в форме строки или платежи, которые ещё не отправлены на сервер */
  hasUnsaved: (values: TStringMap<any>) => boolean;

  /** Последнее состояние строки с сервера в том же виде, в каком его держит форма */
  formValues: (section: string, spending: TStringMap<any>) => TStringMap<any> | null;

  /**
   * Подробности фактических оплат строки (платёжки, договор, раздел). Один запрос на строку,
   * ответ запоминается, пока не изменится набор оплат
   */
  paymentsInfo: (spendingId: string, actualGuids: string[]) => Promise<SpendingPaymentInfo[] | null>;
}

export function createSpendingSaver(store: OrdersStoreType): SpendingSaver {
  // ключ — строка и её оплаты: новая оплата или корректировка дают новый ключ и новый запрос
  const infoCache = new Map<string, Promise<SpendingPaymentInfo[] | null>>();

  const list = (section: string): any[] => {
    const details: any = store.details;

    switch (section) {
      case SPENDING_SECTIONS.other:
        return details.otherOrderSpendings;
      case SPENDING_SECTIONS.trip:
        return details.tripOrderSpendings;
      default:
        return details.outsourcedOrderSpendins;
    }
  };

  /** Последнее состояние строки, полученное с сервера */
  const savedRow = (section: string, id: string) => {
    const found = list(section).find((s: any) => s.id === id);

    return found ? { ...(getSnapshot(found) as any), outsourcerId: found.outsourcerId } : null;
  };

  const savedPayment = (section: string, spendingId: string, guid: string) => {
    const found = list(section).find((s: any) => s.id === spendingId);
    const payment = found ? found.planPayments.find((p: any) => p.guid === guid) : null;

    return payment ? (getSnapshot(payment) as any) : null;
  };

  return {
    rowChanged(section, spending) {
      const saved = savedRow(section, spending.id);

      return saved ? hasChanges(diffSpendingRow(spending, saved)) : true;
    },

    async saveRow(section, spending) {
      const saved: any = savedRow(section, spending.id);

      // строки нет на сервере — сотрудник только что добавил её кнопкой «Добавить»
      if (!saved) {
        const created = await store.createSpendingRow(section, spending.id, spendingFields(spending));

        return !!created;
      }

      const patch = diffSpendingRow(spending, saved);

      if (!hasChanges(patch)) {
        return true;
      }

      return store.saveSpendingRow(spending.id, patch, saved.version);
    },

    async removeRow(section, spending) {
      const saved: any = savedRow(section, spending.id);

      // строка не сохранена на сервере — убирать нечего
      if (!saved) {
        return true;
      }

      return store.removeSpendingRow(spending.id, saved.version);
    },

    paymentChanged(section, spendingId, payment) {
      const saved = savedPayment(section, spendingId, payment.guid);

      return saved ? hasChanges(diffPlanPayment(payment, saved)) : true;
    },

    paymentSaved(section, spendingId, guid) {
      return !!savedPayment(section, spendingId, guid);
    },

    async savePayment(section, spendingId, payment) {
      const saved: any = savedPayment(section, spendingId, payment.guid);

      // платежа нет на сервере — он только что добавлен кнопкой «Добавить оплату»
      if (!saved) {
        return store.addPlanPayment(spendingId, payment.guid, planPaymentFields(payment));
      }

      const patch = diffPlanPayment(payment, saved);

      if (!hasChanges(patch)) {
        return true;
      }

      return store.savePlanPayment(spendingId, payment.guid, patch, saved.version);
    },

    async savePaymentIn(section, spending, payment) {
      if (!savedRow(section, spending.id)) {
        const created = await this.saveRow(section, spending);

        if (!created) {
          return false;
        }
      }

      return this.savePayment(section, spending.id, payment);
    },

    async removePayment(section, spendingId, payment) {
      const saved: any = savedPayment(section, spendingId, payment.guid);

      if (!saved) {
        return true;
      }

      return store.removePlanPayment(spendingId, payment.guid, saved.version);
    },

    formValues(section, spending) {
      const found = list(section).find((s: any) => s.id === spending.id);

      if (!found) {
        return null;
      }

      // форма держит даты объектами Date, поэтому повторяем преобразование,
      // которое делается при первичной загрузке договора
      const payments = (list: any[]) =>
        list.map((payment: any) => ({ ...(getSnapshot(payment) as any), date: payment.dateAsDate }));

      return {
        ...(getSnapshot(found) as any),
        planPayments: payments(found.planPayments),
        actualPayments: payments(found.actualPayments),
        outsourcerId: found.outsourcerId,
        startDate: found.startDateAsDate,
        label: spending.label,
      };
    },

    hasUnsaved(values) {
      const sections: Array<[string, string]> = [
        [SPENDING_SECTIONS.outsourced, "outsourcedOrderSpendins"],
        [SPENDING_SECTIONS.other, "otherOrderSpendings"],
        [SPENDING_SECTIONS.trip, "tripOrderSpendings"],
      ];

      return sections.some(([section, field]) => {
        const rows: any[] = values[field] || [];

        return rows.some((row) => {
          if (this.rowChanged(section, row)) {
            return true;
          }

          const payments: any[] = row.planPayments || [];

          return payments.some((payment) => this.paymentChanged(section, row.id, payment));
        });
      });
    },

    paymentsInfo(spendingId, actualGuids) {
      const key = `${spendingId}|${actualGuids.join(",")}`;
      let cached = infoCache.get(key);

      if (!cached) {
        cached = store.loadSpendingPaymentsInfo(spendingId).then((result: SpendingPaymentInfo[] | null) => {
          // неудачный ответ не запоминаем, чтобы следующее наведение попробовало ещё раз
          if (!result) {
            infoCache.delete(key);
          }

          return result;
        });

        infoCache.set(key, cached);
      }

      return cached;
    },

    rowAuthor(section, spendingId) {
      const saved: any = savedRow(section, spendingId);

      if (!saved || !saved.modifiedByName) {
        return "";
      }

      const when = saved.lastModified ? `, ${saved.lastModified}` : "";
      const where = saved.modifiedFrom ? ` (${saved.modifiedFrom})` : "";

      return `Изменил: ${saved.modifiedByName}${when}${where}`;
    },
  };
}
